/**
 * Utility functions for handling DMM-specific column operations
 */

import { NewValueParams } from 'ag-grid-community';
import {
  BUSINESS_SEGMENT_OPTIONAL_FIELDS,
  DMM_BUSINESS_SEGMENT_ORDER,
  DMM_FIELDS_MAPPING,
  ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS,
  ECHO_PRODUCT_LINE_CODE
} from 'src/constants/business-group-constants';
import { CountryCompanyMapping, DMMBusinessSegmentType, DMMProductLineCampaignFlatten } from 'src/models/DmmModel';

/**
 * Gets unique dropdown values for DMM business segments based on campaign data
 *
 * @param filteredCampaigns - Array of campaign data to extract dropdown values from
 * @param businessSegmentName - The name of the business segment to get values for
 * @returns Array of unique, sorted dropdown values
 */
export const getDMMMasterSegmentDropDowns = (filteredCampaigns: DMMProductLineCampaignFlatten[], businessSegmentName: string): string[] => {
  if (!filteredCampaigns?.length) {
    return [];
  }

  const field = DMM_FIELDS_MAPPING[businessSegmentName as keyof typeof DMM_FIELDS_MAPPING];

  if (!field) {
    return [];
  }

  return getUniqueStringValues(filteredCampaigns.map((campaign) => campaign[field]));
};

/**
 * Extracts unique, non-empty string values from an array and sorts them
 *
 * @param values - Array of values to filter and deduplicate
 * @returns Array of unique, sorted string values
 */
const getUniqueStringValues = (values: any[]): string[] => {
  return [
    ...new Set(values.filter((value): value is string => value !== null && value !== undefined && typeof value === 'string' && value.trim() !== ''))
  ].sort();
};

/**
 * Filters campaigns based on previously selected values in the row
 *
 * @param campaigns - Array of all available campaigns
 * @param rowData - Current row data containing selected values
 * @param currentField - The current business segment field being processed
 * @returns Filtered array of campaigns matching previous selections
 */
export const getFilteredCampaigns = (
  campaigns: DMMProductLineCampaignFlatten[],
  rowData: any,
  currentField: DMMBusinessSegmentType
): DMMProductLineCampaignFlatten[] => {
  if (!campaigns?.length) {
    return [];
  }

  let filteredCampaigns = [...campaigns];
  const currentIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(currentField);

  // Apply filters based on previous selections
  for (let i = 0; i < currentIndex; i++) {
    const previousField = DMM_BUSINESS_SEGMENT_ORDER[i];
    const selectedValue = rowData[previousField];

    if (selectedValue) {
      const campaignField = DMM_FIELDS_MAPPING[previousField];
      filteredCampaigns = filteredCampaigns.filter((campaign) => campaign[campaignField] === selectedValue);
    }
  }

  return filteredCampaigns;
};

/**
 * Resets all subsequent business segment fields when a field value changes in DMM group
 *
 * @param params - The cell value change event parameters
 * @param currentField - The current field being changed
 * @param isDMMGroup - Boolean indicating if this is a DMM group
 */
export const resetSubsequentBusinessSegmentFields = (params: NewValueParams<any, any>, currentField: string, isDMMGroup: boolean): void => {
  // Early return if conditions aren't met
  if (!isDMMGroup || !DMM_BUSINESS_SEGMENT_ORDER.includes(currentField as DMMBusinessSegmentType) || !params.node) {
    return;
  }

  const currentIndex = DMM_BUSINESS_SEGMENT_ORDER.indexOf(currentField as DMMBusinessSegmentType);

  // Return if this is the last field in the order
  if (currentIndex >= DMM_BUSINESS_SEGMENT_ORDER.length - 1) {
    return;
  }

  // Get all fields that come after the current field
  const fieldsToReset = DMM_BUSINESS_SEGMENT_ORDER.slice(currentIndex + 1);

  // Reset each subsequent field to null
  fieldsToReset.forEach((field) => {
    params.node?.setDataValue(field, null);
  });

  // Refresh the cells to update edit state
  params.api.refreshCells({
    force: true,
    columns: fieldsToReset
  });
};

/**
 * Checks if all mandatory fields are filled for a given row of data based on product line
 *
 * @param data - The row data object containing business segment fields
 * @param productLine - The product line code to determine which fields are optional
 * @returns True if all mandatory fields are filled, false otherwise
 */
export const areAllMandatoryFieldsFilled = (data: any, productLine: string): boolean => {
  if (!data || !productLine) {
    return false;
  }

  const optionalFields = productLine === ECHO_PRODUCT_LINE_CODE ? ECHO_BUSINESS_SEGMENT_OPTIONAL_FIELDS : BUSINESS_SEGMENT_OPTIONAL_FIELDS;

  const mandatoryFields = DMM_BUSINESS_SEGMENT_ORDER.filter((field) => !optionalFields.includes(field));

  return mandatoryFields.every((field) => data[field] && data[field] !== '');
};

/**
 * Gets the default value for an optional field based on existing mandatory field values
 *
 * @param field - The optional field name to get the default value for
 * @param data - The current row data containing all business segment values
 * @param campaignsData - Array of campaign data to match against
 * @returns The default value if a unique match is found, null otherwise
 */
export const getDefaultOptionalValue = (field: DMMBusinessSegmentType, data: any, campaignsData: DMMProductLineCampaignFlatten[]): string | null => {
  if (!data || !campaignsData?.length) {
    return null;
  }

  const filteredCampaigns = campaignsData.filter((campaign) => {
    // Get all filled fields from the current data (excluding the current field)
    const filledFields = Object.entries(DMM_FIELDS_MAPPING).filter(([businessField]) => data[businessField] && businessField !== field);

    // Check if all filled fields match the campaign
    return filledFields.every(
      ([businessField, campaignField]) => campaign[campaignField as keyof DMMProductLineCampaignFlatten] === data[businessField]
    );
  });

  // Only return a value if exactly one matching campaign is found
  if (filteredCampaigns.length === 1) {
    const campaignField = DMM_FIELDS_MAPPING[field];
    return filteredCampaigns[0][campaignField as keyof DMMProductLineCampaignFlatten] as string;
  }

  return null;
};

/**
 * Retrieves the country code associated with a given company code
 *
 * @param companyCode - The company code to look up
 * @param countryCompanyMapping - Array of country-company mapping objects
 * @returns The corresponding country code if found, empty string otherwise
 */
export const getCountryCodeFromCompany = (companyCode: string, countryCompanyMapping: CountryCompanyMapping[]): string => {
  if (!companyCode || !countryCompanyMapping?.length) {
    return '';
  }

  const mapping = countryCompanyMapping.find((mapping) => mapping.company_code === companyCode);

  return mapping?.country_code || '';
};
